import mammoth from "mammoth"

const PDF_SCRIPT_URL = "https://app.wellcomefit.ch/img/js/pdf.min.js"
const PDF_WORKER_URL = "https://app.wellcomefit.ch/img/js/pdf.worker.min.js"
const MAX_PDF_PAGES = 8
const MAX_PAGE_WIDTH = 1800
const MAX_PAGE_HEIGHT = 2400

let pdfJsPromise = null

function getExtension(fileName = "") {
  const match = fileName.toLowerCase().match(/\.([a-z0-9]+)$/)
  return match ? match[1] : ""
}

function getPdfLibrary() {
  return window.pdfjsLib || window["pdfjs-dist/build/pdf"] || null
}

function configurePdfLibrary(pdfJs) {
  if (pdfJs && pdfJs.GlobalWorkerOptions) pdfJs.GlobalWorkerOptions.workerSrc = PDF_WORKER_URL
  return pdfJs
}

function loadPdfJs() {
  const existing = getPdfLibrary()
  if (existing) return Promise.resolve(configurePdfLibrary(existing))
  if (pdfJsPromise) return pdfJsPromise

  pdfJsPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script")
    script.src = PDF_SCRIPT_URL
    script.async = true
    script.dataset.documentAiPdf = "true"
    script.onload = () => {
      const pdfJs = getPdfLibrary()
      if (!pdfJs) {
        pdfJsPromise = null
        reject(new Error("PDF-Modul wurde geladen, ist aber nicht verfügbar."))
        return
      }
      resolve(configurePdfLibrary(pdfJs))
    }
    script.onerror = () => {
      pdfJsPromise = null
      reject(new Error("PDF-Modul konnte nicht geladen werden."))
    }
    document.head.appendChild(script)
  })

  return pdfJsPromise
}

function canvasToBlob(canvas) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob)
        else reject(new Error("PDF-Seite konnte nicht in ein Bild umgewandelt werden."))
      },
      "image/jpeg",
      0.86,
    )
  })
}

function getPageNumbers(pageCount) {
  if (pageCount <= MAX_PDF_PAGES) return Array.from({ length: pageCount }, (_, index) => index + 1)
  return [...Array.from({ length: MAX_PDF_PAGES - 1 }, (_, index) => index + 1), pageCount]
}

async function renderPdfPages(file) {
  const pdfJs = await loadPdfJs()
  const loadingTask = pdfJs.getDocument({ data: await file.arrayBuffer() })
  const pdf = await loadingTask.promise
  const pageFiles = []

  try {
    for (const pageNumber of getPageNumbers(pdf.numPages)) {
      const page = await pdf.getPage(pageNumber)
      const canvas = document.createElement("canvas")

      try {
        const baseViewport = page.getViewport({ scale: 1 })
        const scale = Math.min(1.6, MAX_PAGE_WIDTH / baseViewport.width, MAX_PAGE_HEIGHT / baseViewport.height)
        const viewport = page.getViewport({ scale })
        const context = canvas.getContext("2d", { alpha: false })
        if (!context) throw new Error("PDF-Seite konnte nicht vorbereitet werden.")

        canvas.width = Math.ceil(viewport.width)
        canvas.height = Math.ceil(viewport.height)
        context.fillStyle = "#ffffff"
        context.fillRect(0, 0, canvas.width, canvas.height)

        await page.render({ canvasContext: context, viewport }).promise
        const blob = await canvasToBlob(canvas)
        const baseName = file.name.replace(/\.[^/.]+$/, "") || "document"
        pageFiles.push(new File([blob], `${baseName}-page-${pageNumber}.jpg`, { type: "image/jpeg" }))
      } finally {
        page.cleanup()
        canvas.width = 1
        canvas.height = 1
      }
    }
  } finally {
    if (pdf.cleanup) pdf.cleanup()
    if (pdf.destroy) await pdf.destroy()
  }

  return pageFiles
}

async function extractText(file, extension) {
  if (extension === "docx") {
    const result = await mammoth.extractRawText({ arrayBuffer: await file.arrayBuffer() })
    return result.value || ""
  }

  if (["txt", "csv", "html", "htm", "xml", "json"].includes(extension) || (file.type || "").startsWith("text/")) {
    return file.text()
  }

  return ""
}

async function buildAnalysisFormData(file) {
  if (!file) throw new Error("Keine Datei für die AI-Analyse ausgewählt.")

  const extension = getExtension(file.name)
  const formData = new FormData()
  formData.append("fileName", file.name || "Dokument")

  if (file.type === "application/pdf" || extension === "pdf") {
    const pages = await renderPdfPages(file)
    pages.forEach((page) => formData.append("files", page))
    return formData
  }

  if ((file.type || "").startsWith("image/") || ["jpg", "jpeg", "png", "webp", "gif"].includes(extension)) {
    formData.append("files", file)
    return formData
  }

  const text = await extractText(file, extension)
  if (text.trim()) {
    formData.append("text", text.slice(0, 24000))
    return formData
  }

  throw new Error("Dieses Dateiformat kann noch nicht automatisch analysiert werden. Unterstützt sind PDF, Bilder, DOCX und Textdateien.")
}

function getErrorMessage(error) {
  return error && error.response && error.response.data && error.response.data.error
    ? error.response.data.error
    : error && error.message
      ? error.message
      : "Die AI-Analyse ist fehlgeschlagen."
}

export default {
  buildAnalysisFormData,
  getErrorMessage,
}
